import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';

export const getPrescriptionFields = createAsyncThunk(
    'prescriptionField/getPrescriptionFields',
    async (doctorId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/prescription-field/doctor/${doctorId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const createPrescriptionField = createAsyncThunk(
    'prescriptionField/createPrescriptionField',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/prescription-field`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updatePrescriptionField = createAsyncThunk(
    'prescriptionField/updatePrescriptionField',
    async ({ fieldId, payload }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/prescription-field/${fieldId}`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const deletePrescriptionField = createAsyncThunk(
    'prescriptionField/deletePrescriptionField',
    async (fieldId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/prescription-field/${fieldId}`);
            return { ...response.data, fieldId };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const reorderPrescriptionFields = createAsyncThunk(
    'prescriptionField/reorderPrescriptionFields',
    async ({ doctorId, order }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/prescription-field/reorder`, { doctorId, order });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateAppointmentCustomNote = createAsyncThunk(
    'prescriptionField/updateAppointmentCustomNote',
    async ({ appointmentId, fieldId, value }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.patch(
                `${baseURL}/appointment/customNote/${appointmentId}`,
                { fieldId, value }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const prescriptionFieldSlice = createSlice({
    name: 'prescriptionField',
    initialState: {
        fields: [],
        // The doctor these fields belong to — a screen for another doctor must
        // not render them.
        fieldsFor: null,
        loading: false,
        // The list itself could not be loaded (distinct from a failed write).
        loadError: null,
        submitting: false,
        deletingId: null,
        // The order on screen before an optimistic reorder, restored if the
        // server refuses it.
        orderBeforeReorder: null,
        error: null,
    },
    reducers: {
        clearPrescriptionFieldError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getPrescriptionFields.pending, (state, action) => {
                if (state.fieldsFor !== action.meta.arg) state.fields = [];
                state.fieldsFor = action.meta.arg;
                state.loading = true;
                state.loadError = null;
                state.error = null;
            })
            .addCase(getPrescriptionFields.fulfilled, (state, action) => {
                if (state.fieldsFor && action.meta.arg !== state.fieldsFor) return;
                state.loading = false;
                state.fields = action.payload?.data || [];
            })
            .addCase(getPrescriptionFields.rejected, (state, action) => {
                if (state.fieldsFor && action.meta.arg !== state.fieldsFor) return;
                state.loading = false;
                state.fields = [];
                state.loadError = action.payload ?? action.error?.message ?? 'failed';
                state.error = action.payload;
            })
            .addCase(createPrescriptionField.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(createPrescriptionField.fulfilled, (state, action) => {
                state.submitting = false;
                if (action.payload?.data) {
                    state.fields.push(action.payload.data);
                }
            })
            .addCase(createPrescriptionField.rejected, (state, action) => {
                state.submitting = false;
                state.error = action.payload;
            })
            .addCase(updatePrescriptionField.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(updatePrescriptionField.fulfilled, (state, action) => {
                state.submitting = false;
                const updated = action.payload?.data;
                if (updated) {
                    const idx = state.fields.findIndex((f) => f.fieldId === updated.fieldId);
                    if (idx !== -1) state.fields[idx] = updated;
                }
            })
            .addCase(updatePrescriptionField.rejected, (state, action) => {
                state.submitting = false;
                state.error = action.payload;
            })
            .addCase(deletePrescriptionField.pending, (state, action) => {
                state.deletingId = action.meta.arg;
            })
            .addCase(deletePrescriptionField.fulfilled, (state, action) => {
                state.deletingId = null;
                const id = action.payload?.fieldId || action.payload?.data?.fieldId;
                if (id) {
                    state.fields = state.fields.filter((f) => f.fieldId !== id);
                }
            })
            .addCase(deletePrescriptionField.rejected, (state, action) => {
                state.deletingId = null;
                state.error = action.payload;
            })
            // Reorder is optimistic: the list moves at once, and a refusal puts
            // it back exactly as it was.
            .addCase(reorderPrescriptionFields.pending, (state, action) => {
                const order = action.meta.arg?.order || [];
                state.orderBeforeReorder = state.fields.map((f) => ({ ...f }));
                const byId = new Map(state.fields.map((f) => [f.fieldId, f]));
                const moved = order.map((id) => byId.get(id)).filter(Boolean);
                const rest = state.fields.filter((f) => !order.includes(f.fieldId));
                state.fields = [...moved, ...rest];
            })
            .addCase(reorderPrescriptionFields.fulfilled, (state, action) => {
                state.orderBeforeReorder = null;
                if (action.payload?.data) {
                    state.fields = action.payload.data;
                }
            })
            .addCase(reorderPrescriptionFields.rejected, (state, action) => {
                if (state.orderBeforeReorder) state.fields = state.orderBeforeReorder;
                state.orderBeforeReorder = null;
                state.error = action.payload;
            });
    },
});

export const { clearPrescriptionFieldError } = prescriptionFieldSlice.actions;
export default prescriptionFieldSlice.reducer;
